/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import type { AnyAction } from '@reduxjs/toolkit';
import type { RootStateOrAny } from 'react-redux';
import reduceReducers from 'reduce-reducers';
import { combineReducers } from 'redux';

import { reducer as actions } from '@ibm-watson/shared/modules/actions/store/actions/reducers';
import { reducer as boosts } from '@ibm-watson/shared/modules/actions/store/boosts/reducers';
import { collectionsReducer as collections } from '@ibm-watson/shared/modules/actions/store/collections/reducers';
import { default as convoSkills } from '@ibm-watson/shared/modules/actions/store/convo-skills/reducer';
import { reducer as dataTypes } from '@ibm-watson/shared/modules/actions/store/data-types/reducers';
import { reducer as entities } from '@ibm-watson/shared/modules/actions/store/entities/reducers';
import { reducer as intents } from '@ibm-watson/shared/modules/actions/store/intents/reducers';
import { reducer as actionsModels } from '@ibm-watson/shared/modules/actions/store/models/actions/reducers';
import { reducer as systemDataTypes } from '@ibm-watson/shared/modules/actions/store/system-data-types/reducer';
import { reducer as systemEntities } from '@ibm-watson/shared/modules/actions/store/system-entities/reducers';
import { reducer as variables } from '@ibm-watson/shared/modules/actions/store/variables/reducers';
import { reducer as workspacestate } from '@ibm-watson/shared/modules/actions/store/workspacestate/reducers';
import { default as analytics } from '@ibm-watson/shared/modules/analytics/store/reducers';
import { default as catalog } from '@ibm-watson/shared/modules/channels/store/catalog/reducer';
import { default as integrations } from '@ibm-watson/shared/modules/channels/store/channels/reducer';
import { default as parsedOpenAPIExtensions } from '@ibm-watson/shared/modules/channels/store/parsedOpenAPIExtensions/reducer';
import { default as voice } from '@ibm-watson/shared/modules/channels/store/voice/reducer';
import { default as customService } from '@ibm-watson/shared/modules/custom-service/store/reducer';
import { reducer as dialogAnalytics } from '@ibm-watson/shared/modules/dialog/store/analytics/reducers';
import { reducer as capabilities } from '@ibm-watson/shared/modules/dialog/store/capabilities/reducers';
import { reducer as conflicts } from '@ibm-watson/shared/modules/dialog/store/conflicts/reducers';
import { reducer as dialog } from '@ibm-watson/shared/modules/dialog/store/dialog/reducers';
import { reducer as dialogTryIt } from '@ibm-watson/shared/modules/dialog/store/dialog-try-it/reducers';
import { reducer as recommendations } from '@ibm-watson/shared/modules/dialog/store/recommendations/reducers';
import { reducer as search } from '@ibm-watson/shared/modules/dialog/store/search/reducers';
import { reducer as connectors } from '@ibm-watson/shared/modules/dialog/store/webhook-connectors/reducers';
import { reducer as workspaces } from '@ibm-watson/shared/modules/dialog/store/workspaces/reducers';
import { default as discovery } from '@ibm-watson/shared/modules/discovery/store/reducer';
import { default as elastic } from '@ibm-watson/shared/modules/elastic-search/store/reducer';
import { default as accessControl } from '@ibm-watson/shared/modules/ia/store/access-control/reducer';
import { default as activityLog } from '@ibm-watson/shared/modules/ia/store/activity-log/reducer';
import { default as aiModels } from '@ibm-watson/shared/modules/ia/store/aiModels/reducer';
import { default as assistants } from '@ibm-watson/shared/modules/ia/store/assistants/reducer';
import { default as environments } from '@ibm-watson/shared/modules/ia/store/environments/reducer';
import { reducer as features } from '@ibm-watson/shared/modules/ia/store/features/reducers';
import { default as header } from '@ibm-watson/shared/modules/ia/store/header/reducer';
import { default as insightActions } from '@ibm-watson/shared/modules/ia/store/insight-actions/reducer';
import { default as instance } from '@ibm-watson/shared/modules/ia/store/instance/reducer';
import { default as instances } from '@ibm-watson/shared/modules/ia/store/instances/reducers';
import { reducer as messages } from '@ibm-watson/shared/modules/ia/store/messages/reducers';
import { reducer as models } from '@ibm-watson/shared/modules/ia/store/models';
import { default as multilingual } from '@ibm-watson/shared/modules/ia/store/multilingual/reducer';
import { default as onboarding } from '@ibm-watson/shared/modules/ia/store/onboarding/reducer';
import { reducer as plan } from '@ibm-watson/shared/modules/ia/store/plan/reducers';
import { default as portals } from '@ibm-watson/shared/modules/ia/store/portals/reducer';
import { default as preferences } from '@ibm-watson/shared/modules/ia/store/preferences/reducer';
import { default as preview } from '@ibm-watson/shared/modules/ia/store/preview/reducer';
import { default as publish } from '@ibm-watson/shared/modules/ia/store/publish/reducer';
import { default as releases } from '@ibm-watson/shared/modules/ia/store/releases/reducer';
import { reducer as session } from '@ibm-watson/shared/modules/ia/store/session/reducers';
import { default as skills } from '@ibm-watson/shared/modules/ia/store/skills/reducer';
import { default as snapshotsSkills } from '@ibm-watson/shared/modules/ia/store/snapshots/reducer';
import { default as templates } from '@ibm-watson/shared/modules/ia/store/templates/reducer';
import { default as milvus } from '@ibm-watson/shared/modules/milvus/store/reducer';
import { reducer as discoveryClassic } from '@ibm-watson/shared/store/discovery/reducers';
import { reducer as navigation } from '@ibm-watson/shared/store/navigation';
import { reducer as searchSkill } from '@ibm-watson/shared/store/searchSkill/reducers';
import { reducer as webChat } from '@ibm-watson/shared/store/web-chat/reducers';
import type { RootState } from '../ts/state';
import { StoreActionType } from './types';
import type { ResetAction } from './types';

const reducers = {
  accessControl,
  actions,
  activityLog,
  aiModels,
  analytics,
  assistants,
  boosts,
  capabilities,
  catalog,
  collections,
  conflicts,
  connectors,
  convoSkills,
  customService,
  dataTypes,
  dialog,
  dialogAnalytics,
  dialogTryIt,
  discovery,
  // This reducer is specific to Classic IA.
  discoveryClassic,
  elastic,
  entities,
  environments,
  features,
  header,
  insightActions,
  instance,
  instances,
  integrations,
  intents,
  milvus,
  messages,
  models: reduceReducers(models, actionsModels),
  multilingual,
  // This reducer is specific to Classic IA.
  navigation,
  onboarding,
  parsedOpenAPIExtensions,
  plan,
  portals,
  preferences,
  preview,
  publish,
  recommendations,
  releases,
  search,
  // This reducer is specific to Classic IA.
  searchSkill,
  session,
  skills,
  snapshotsSkills,
  systemDataTypes,
  systemEntities,
  templates,
  variables,
  voice,
  // This reducer is specific to Classic IA.
  webChat,
  workspaces,
  workspacestate
};

// Combine the reducers that make up the entire application.
const appReducer = combineReducers(reducers);

// Create the root reducer, which in most cases will simply dispatch the
// specified action to the application reducers.
// However, if it detects the special action that resets the Redux store, it
// will set the store's slices to their default, uninitialized state.
// The exceptions are the session slice (to prevent the user from needing to
// log in again), the instance slice (so that we do not need to re-retrieve
// instance information) and the access control information that relates to
// the instance (which is not changing).
const rootReducer = (state: RootState, action: AnyAction) => {
  if (action.type === StoreActionType.RESET_REDUX) {
    const resetAction = action as ResetAction;
    const { new_ia } = resetAction.payload;

    // A Redux store will return the initialized slice if an undefined state
    // is passed to it, so by doing this we get a Redux state where every
    // slice is in its freshly-initialized state.
    const initializedState = appReducer(undefined, action);

    return {
      ...initializedState,
      accessControl: {
        ...initializedState.accessControl,
        instance: state.accessControl.instance
      },
      instance: {
        ...state.instance,
        new_ia
      },
      session: {
        ...state.session,
        // Now that the Redux store reset has completed, indicate that the
        // session slice has been initialized and the instance routes can
        // now render as usual.
        initialized: true
      }
    } as any;
  }

  return appReducer(state as RootStateOrAny, action);
};

export default rootReducer;
